/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  CashlinkWalletCreationRequest,
  GenericWalletCreationRequest,
  Wallet,
  WalletBalance,
} from '../models/index';
import {
    CashlinkWalletCreationRequestFromJSON,
    CashlinkWalletCreationRequestToJSON,
    GenericWalletCreationRequestFromJSON,
    GenericWalletCreationRequestToJSON,
    WalletFromJSON,
    WalletToJSON,
    WalletBalanceFromJSON,
    WalletBalanceToJSON,
} from '../models/index';

export interface WalletsBalancesListRequest {
    token: string;
    wallet?: string;
}

export interface WalletsCashlinkWalletCreateRequest {
    cashlinkWalletCreationRequest?: CashlinkWalletCreationRequest;
}

export interface WalletsGenericWalletCreateRequest {
    genericWalletCreationRequest: GenericWalletCreationRequest;
}

export interface WalletsRetrieveRequest {
    id: string;
}

/**
 * 
 */
export class WalletsApi extends runtime.BaseAPI {

    /**
     * Get the balance of the given wallet for the given product.
     */
    async walletsBalancesListRaw(requestParameters: WalletsBalancesListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<WalletBalance>>> {
        if (requestParameters['token'] == null) {
            throw new runtime.RequiredError(
                'token',
                'Required parameter "token" was null or undefined when calling walletsBalancesList().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['token'] != null) {
            queryParameters['token'] = requestParameters['token'];
        }

        if (requestParameters['wallet'] != null) {
            queryParameters['wallet'] = requestParameters['wallet'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/wallets/balances/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(WalletBalanceFromJSON));
    }

    /**
     * Get the balance of the given wallet for the given product.
     */
    async walletsBalancesList(requestParameters: WalletsBalancesListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<WalletBalance>> {
        const response = await this.walletsBalancesListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Create a Cashlink wallet.
     */
    async walletsCashlinkWalletCreateRaw(requestParameters: WalletsCashlinkWalletCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Wallet>> {
        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/wallets/cashlink-wallet/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: CashlinkWalletCreationRequestToJSON(requestParameters['cashlinkWalletCreationRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => WalletFromJSON(jsonValue));
    }

    /**
     * Create a Cashlink wallet.
     */
    async walletsCashlinkWalletCreate(requestParameters: WalletsCashlinkWalletCreateRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Wallet> {
        const response = await this.walletsCashlinkWalletCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Create or fetch already created wallet for an investor.
     */
    async walletsGenericWalletCreateRaw(requestParameters: WalletsGenericWalletCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Wallet>> {
        if (requestParameters['genericWalletCreationRequest'] == null) {
            throw new runtime.RequiredError(
                'genericWalletCreationRequest',
                'Required parameter "genericWalletCreationRequest" was null or undefined when calling walletsGenericWalletCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/wallets/generic-wallet/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: GenericWalletCreationRequestToJSON(requestParameters['genericWalletCreationRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => WalletFromJSON(jsonValue));
    }

    /**
     * Create or fetch already created wallet for an investor.
     */
    async walletsGenericWalletCreate(requestParameters: WalletsGenericWalletCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Wallet> {
        const response = await this.walletsGenericWalletCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async walletsListRaw(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<Wallet>>> {
        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/wallets/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(WalletFromJSON));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async walletsList(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<Wallet>> {
        const response = await this.walletsListRaw(initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async walletsRetrieveRaw(requestParameters: WalletsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Wallet>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling walletsRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/wallets/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => WalletFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async walletsRetrieve(requestParameters: WalletsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Wallet> {
        const response = await this.walletsRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Create a tangany wallet.
     */
    async walletsTanganyWalletCreateRaw(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Wallet>> {
        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/wallets/tangany-wallet/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => WalletFromJSON(jsonValue));
    }

    /**
     * Create a tangany wallet.
     */
    async walletsTanganyWalletCreate(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Wallet> {
        const response = await this.walletsTanganyWalletCreateRaw(initOverrides);
        return await response.value();
    }

}
